import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { StaticImage } from 'gatsby-plugin-image';
import cls from 'classnames';
import { PublicArrowRightTail, PublicSuccess, PublicLoadingCircle } from 'notta-web-icon';

import CmsKeepWord from '@/components/cms-keep-word';
import { requestNewsLetter } from '@/utils/newsletter';

import * as styles from './index.module.less';

const EmailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;

interface CopywritingInfo {
  title?: string;
  desc?: string;
  emailInputPlaceholder?: string;
  btnText?: string;
  subscribeTip?: string;
  subscribeTipSuccess?: string;
}

const NewsLetterCopywriting: Record<string, CopywritingInfo> = {
  ja: {
    title: '生産力向上<wbr/>の<wbr/>課題解決<wbr/>に<wbr/>繋がる<wbr/>最新DX・<wbr/>AI関連情報<wbr/>を<wbr/>お届け',
    desc: '「<a href="https://www.notta.ai/privacy" target="_blank">プライバシー</a>」<wbr/>の<wbr/>記載<wbr/>に<wbr/>基づいて<wbr/>適切に<wbr/>管理され、<wbr/>目的以外<wbr/>には<wbr/>一切使用しません。',
    emailInputPlaceholder: 'メールアドレスを入力',
    btnText: '送信',
    subscribeTip:
      '「<a href="https://www.notta.ai/privacy" target="_blank">プライバシー</a>」<wbr/>の<wbr/>記載<wbr/>に<wbr/>基づいて<wbr/>適切に<wbr/>管理され、<wbr/>目的以外<wbr/>には<wbr/>一切使用しません。',
    subscribeTipSuccess: '購読が完了しました。ありがとうございます。',
  },
  en: {
    title: 'Ready for More Good Reads?',
    desc: 'Being in the loop means never missing out. Subscribe to our newsletter to receive exclusive content, weekly updates, and more!',
    emailInputPlaceholder: 'Email',
    btnText: 'Subscribe',
    subscribeTip: 'A world of handpicked news, insights, and trending topics are just one subscription away. ',
    subscribeTipSuccess: 'Your subscription has been successful.',
  },
};

enum RequestStatus {
  Normal,
  Success,
  Failed,
}

interface NewsLetterProps {
  /** 界面语种 */
  locale: string;
  /** 外层容器类名 */
  outerClassName: string;
  /** 组件容器类名 */
  classNames?: string;
  /** 是否显示bannerLogo */
  bannerLogo?: boolean;
  /** 是否显示描述文案 */
  desc?: boolean;
  /** 是否显示订阅提示文案 */
  subscribeTip?: boolean;
  /** 内容文案(单语种) */
  copywriting?: CopywritingInfo;
}

const NewsLetter = ({
  classNames,
  locale,
  outerClassName,
  bannerLogo = false,
  desc = false,
  subscribeTip = false,
  copywriting = {},
}: NewsLetterProps) => {
  const [email, setEmail] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState(RequestStatus.Normal);

  const copywritingInfo = { ...NewsLetterCopywriting[locale], ...copywriting };

  const handleChange = (value: string) => {
    setEmail(value);
    setDisabled(!EmailRegex.test(value));
  };

  const onSubmitEmail = debounce(
    async (e) => {
      if (!email || disabled || loading) return;
      // 阻止表单提交导致的页面刷新
      e.preventDefault();
      setLoading(true);

      const res = await requestNewsLetter({ locale, email });

      setLoading(false);

      if (!res.isSuc) {
        setRequestStatus(RequestStatus.Failed);
        return;
      }

      setRequestStatus(RequestStatus.Success);
    },
    500,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    const outerEl = document.querySelector(`.${outerClassName}`);
    const inputEl = outerEl.querySelector(`#${outerClassName}-newsletter-form input`);

    if (!inputEl) return;

    // 处理表单提交被阻止的情况
    const handleInvalid = () => {
      setDisabled(true);
    };

    inputEl.addEventListener('invalid', handleInvalid);

    return () => {
      inputEl.removeEventListener('invalid', handleInvalid);
    };
  }, [outerClassName]);

  return (
    <div key="newsletter" className={cls(styles.newsletter_wrap, classNames)}>
      <div className={cls(styles.newsletter_banner, 'newsletter_banner')}>
        {bannerLogo && (
          <img
            src="https://www.notta.ai/pictures/Notta-Newsletter-Img.png"
            alt="Notta-Newsletter-Img"
            className={cls(styles.newsletter_img, 'newsletter_img')}
          />
        )}

        <CmsKeepWord className={cls(styles.newsletter_title, 'newsletter_title')} text={copywritingInfo.title} />
      </div>

      {desc && <CmsKeepWord className={cls(styles.newsletter_desc, 'newsletter_desc')} text={copywritingInfo.desc} />}

      {requestStatus === RequestStatus.Normal ? (
        <div className={cls(styles.newsletter_operation_wrap, 'newsletter_operation_wrap')}>
          <form
            id={`${outerClassName}-newsletter-form`}
            className={cls(styles.newsletter_form, 'newsletter_form')}
            action="#"
          >
            <input
              className={cls(styles.newsletter_form_input, 'newsletter_form_input', disabled && styles.input_error)}
              type="email"
              name="email"
              autoComplete="off"
              placeholder={copywritingInfo.emailInputPlaceholder}
              required
              value={email}
              maxLength={30}
              onChange={(e) => handleChange(e.target.value)}
            />
            {disabled && (
              <StaticImage
                src="../../images/validator-error.png"
                width={18}
                alt="Notta"
                className={cls(styles.input_error_icon, 'input_error_icon')}
              />
            )}
          </form>
          <button
            form={`${outerClassName}-newsletter-form`}
            className={cls(styles.newsletter_btn_submit, 'newsletter_btn_submit')}
            data-disabled={disabled || loading}
            type="submit"
            onClick={onSubmitEmail}
          >
            {copywritingInfo.btnText}

            {loading ? (
              <PublicLoadingCircle className={cls(styles.btn_icon, 'btn_icon', styles.animation_spin)} />
            ) : (
              <PublicArrowRightTail className={cls(styles.btn_icon, 'btn_icon')} />
            )}
          </button>
        </div>
      ) : (
        <div className={cls(styles.newsletter_success_wrap, 'newsletter_success_wrap')}>
          <PublicSuccess className={cls(styles.success_icon, 'btn_icon')} color="#2AD194" />
          <div className={cls(styles.success_text, 'success_text')}>{copywritingInfo.subscribeTipSuccess}</div>
        </div>
      )}

      {subscribeTip && (
        <CmsKeepWord className={cls(styles.newsletter_tip, 'newsletter_tip')} text={copywritingInfo.subscribeTip} />
      )}
    </div>
  );
};

export default NewsLetter;
